import React, { useState } from 'react';
import './LoginPage.css';
import TurnstileWidget from './TurnstileWidget';

// Дві сторінки в одній:
//  /reset-password                 -> запит листа (email або Genesis ID)
//  /reset-password?id=...&token=.. -> встановлення нового пароля за посиланням з листа
const API = 'https://orkxz4jpah.execute-api.us-east-1.amazonaws.com/password-reset';

const pwRuleError = (pw) =>
  pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw)
    ? 'Password must be at least 8 characters and contain letters and digits'
    : null;

const box = (type) => ({
  padding: '12px 14px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', textAlign: 'left',
  background: type === 'error' ? '#fdecea' : '#e8f8ee', color: type === 'error' ? '#b42318' : '#166534',
});

function ResetPasswordPage({ onNavigate }) {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('id') || '';
  const token = params.get('token') || '';
  const isSetMode = !!(id && token);

  const [identifier, setIdentifier] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok' | 'error', text }
  const [done, setDone] = useState(false);
  const [tsToken, setTsToken] = useState('');
  const [tsKey, setTsKey] = useState(0);

  const call = async (payload) => {
    const res = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    if (busy) return;
    setMsg(null);
    if (!tsToken) { setMsg({ type: 'error', text: 'Please complete the security check' }); return; }
    setBusy(true);
    try {
      const r = await call({ action: 'request', identifier: identifier.trim(), turnstileToken: tsToken });
      if (r.success) {
        setDone(true);
        setMsg({ type: 'ok', text: 'If an account exists, we have sent an email with a link to set a new password. Check your inbox and spam folder.' });
      } else {
        setMsg({ type: 'error', text: r.error || 'Something went wrong. Please try again.' });
        setTsKey((k) => k + 1); // новий токен для наступної спроби
      }
    } catch {
      setMsg({ type: 'error', text: 'Network error. Please try again.' });
      setTsKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const handleSet = async (e) => {
    e.preventDefault();
    if (busy) return;
    setMsg(null);
    const ruleErr = pwRuleError(pw1);
    if (ruleErr) { setMsg({ type: 'error', text: ruleErr }); return; }
    if (pw1 !== pw2) { setMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    setBusy(true);
    try {
      const r = await call({ action: 'confirm', genesisID: id, token, newPassword: pw1 });
      if (r.success) {
        setDone(true);
        setMsg({ type: 'ok', text: 'Your password has been updated. You can log in now.' });
      } else {
        setMsg({ type: 'error', text: r.error || 'Something went wrong. Please try again.' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const linkBtn = { background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', fontSize: '0.9rem', padding: 0 };

  return (
    <div className="login-container">
      <div className="login-form">
        <h2 style={{ fontSize: '1.6rem' }}>{isSetMode ? 'Set New Password' : 'Forgot Password?'}</h2>

        {msg && <div style={box(msg.type)}>{msg.text}</div>}

        {!done && !isSetMode && (
          <form onSubmit={handleRequest}>
            <p style={{ fontSize: '14px', color: '#555', marginTop: 0, textAlign: 'left' }}>
              Enter your email or Genesis ID. We will email you a link to set a new password.
              If you forgot your Genesis ID, enter your email – the letter will list all your accounts.
            </p>
            <div className="form-group">
              <input
                type="text"
                placeholder="Email or Genesis ID"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <TurnstileWidget key={tsKey} onToken={setTsToken} />
            <div className="form-actions">
              <button type="submit" className="btn-login" disabled={busy}>
                {busy ? 'Sending...' : 'Send link'}
              </button>
            </div>
          </form>
        )}

        {!done && isSetMode && (
          <form onSubmit={handleSet}>
            <p style={{ fontSize: '14px', color: '#555', marginTop: 0, textAlign: 'left' }}>
              Genesis ID: <strong>{id}</strong>
            </p>
            {/* для менеджерів паролів */}
            <input type="text" name="username" value={id} autoComplete="username" readOnly hidden />
            <div className="form-group">
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="New password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="Repeat new password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <div style={{ textAlign: 'left', marginBottom: '16px' }}>
              <button type="button" style={linkBtn} onClick={() => setShowPw(!showPw)}>
                {showPw ? '🙈 Hide password' : '👁 Show password'}
              </button>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>Minimum 8 characters, letters and digits</div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-login" disabled={busy}>
                {busy ? 'Saving...' : 'Save new password'}
              </button>
            </div>
          </form>
        )}

        <div className="form-actions" style={{ marginTop: '10px' }}>
          {(done && isSetMode) ? (
            <button type="button" className="btn-login" onClick={() => onNavigate('login')}>Go to Login</button>
          ) : (
            <button type="button" className="btn-back" onClick={() => onNavigate('login')}>Back to Login</button>
          )}
          {isSetMode && !done && (
            <button type="button" style={{ ...linkBtn, marginTop: '6px' }} onClick={() => onNavigate('reset-password')}>
              Link expired? Request a new one
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
